import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useA11ySelector } from '@sg/a11y-helpers';

import { Home } from '@order/features/home';
import { useTelemetry } from '@order/Telemetry';

import { FloatingButtons } from '../../../../components';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { type useHomeContent, useHomeNavigation } from '../../hooks';
import { HomeLoyaltyButton } from '../HomeLoyaltyButton';
import { HomeScreenBanner } from '../HomeScreenBanner';

export const HomeScreenContentXS = (props: HomeScreenContentProps) => {
  const {
    homeContent,
    bagPressed,
    ctaPressed,
    secondaryCtaLabel,
    secondaryCtaPressed,
    shouldRenderSecondaryCta,
  } = props;

  const { track } = useTelemetry();
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const { navigateToAccount, navigateToLoyalty } = useHomeNavigation();

  const a11ySelector = useA11ySelector('home.container');
  const contentA11ySelector = useA11ySelector('home.hero.text-content');
  const imageA11ySelector = useA11ySelector('home.hero.img');

  const shouldRenderScanButton = isLoggedIn;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onScanPress = useCallback(() => {
    router.navigate('/scan-at-checkout');

    track('home.scan.cta_tapped');
  }, [router, track]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Home.ContainerXS {...a11ySelector.props}>
        <Home.HeroImageXS
          imageUrl={homeContent.mainImageXS}
          imageA11yLabel={homeContent.mainImageA11yXS}
          {...imageA11ySelector.props}
        >
          <Home.ContentWrapperXS {...contentA11ySelector.props}>
            <Home.HeaderXS
              onPressAccount={navigateToAccount}
              onPressScan={shouldRenderScanButton ? onScanPress : undefined}
            >
              <Home.EyebrowText
                text={homeContent.eyebrowText}
                fontColor={homeContent.eyebrowTextColorXS}
              />

              <Home.HeroText text={homeContent.heroText} fontColor={homeContent.heroTextColorXS} />

              {homeContent.infoText ? (
                <Home.InfoText
                  text={homeContent.infoText}
                  fontColor={homeContent.infoTextColorXS}
                />
              ) : null}

              <Home.HeaderCta
                accessibilityLabel={homeContent.ctaText}
                deepLink={homeContent.ctaDeepLink}
                onPressCta={ctaPressed}
              >
                {homeContent.ctaText}
              </Home.HeaderCta>

              {shouldRenderSecondaryCta ? (
                <Home.HeaderCta
                  accessibilityLabel={secondaryCtaLabel}
                  onPressCta={secondaryCtaPressed}
                >
                  {secondaryCtaLabel}
                </Home.HeaderCta>
              ) : null}
            </Home.HeaderXS>
          </Home.ContentWrapperXS>
        </Home.HeroImageXS>
      </Home.ContainerXS>

      <FloatingButtons.Container anchor="full">
        <Home.FloatingButtons>
          <HomeLoyaltyButton fallbackMessage={homeContent.ctaLoyalty} onPress={navigateToLoyalty} />

          <FloatingButtons.Bag showMessage={false} onPress={bagPressed} />
        </Home.FloatingButtons>

        <HomeScreenBanner />
      </FloatingButtons.Container>
    </>
  );
};

type HomeScreenContentProps = {
  homeContent: ReturnType<typeof useHomeContent>['homeContent'];
  ctaPressed: () => void;
  bagPressed: () => void;
  shouldRenderSecondaryCta: boolean;
  secondaryCtaLabel: string;
  secondaryCtaPressed: () => void;
};
